import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import supabase from "../supabase/supabase";
import { listPatients } from "../lib/demographic";

const { FiSearch, FiAlertTriangle, FiCheck, FiClock, FiActivity, FiRefreshCw } =
  FiIcons;

/**
 * Item 6.5 (Phase 5 security remediation, 24–25/08/2026)
 *
 * Patient identity listing now comes from the governed backend route
 * (GET /api/v1/users via lib/demographic.js's listPatients), which
 * resolves platform_user -> patient_identity_link -> patient server-
 * side and is tenant-scoped. This replaces a prior direct client-side
 * Supabase query against the legacy `users` table.
 *
 * The emergency_event_history_2024 query below is UNCHANGED and out of
 * scope for this migration — it was not named in the Item 6 directive.
 * It has no tenant_id filter; flagged separately for governance review,
 * not fixed here.
 *
 * ALERT-MATCHING CAVEAT — investigated 25/08/2026, NOT resolved, do not
 * treat as fixed:
 *
 * getPatientAlerts() below matches activeAlerts[].patientId and
 * emergency_event_history_2024.patient_id against a patient identifier.
 * Tracing every backend writer that produces these values found the
 * identifier convention is NOT consistent across the codebase:
 *
 *   - server/services/ingestion.js's /vitals route takes `patientId`
 *     directly from req.body with NO validation against any table —
 *     it could be any string the caller sends. This is also a separate,
 *     unresolved cross-tenant concern (no ownership/tenant check),
 *     flagged to AbdulRafiu outside of Item 6.5.
 *   - server/services/wearableBridge.js uses `standardizedData.patientId`
 *     for BOTH a devices insert (FK-enforced to canonical patient.id)
 *     AND a users table update (`.eq("id", standardizedData.patientId)`,
 *     implying legacy users.id) in the same function — internally
 *     inconsistent, likely a pre-existing bug upstream of this file.
 *   - emergency_event_history_2024 has no writer anywhere in this repo
 *     and no FK constraint; its `patient_id` semantics cannot be
 *     verified from the codebase at all.
 *
 * Given this, there is no single identifier this file can match against
 * with confidence. `patientId` (canonical, from GET /api/v1/users) is
 * used below as the best available default — it is at least correct
 * for the one identifier space that IS verified (devices, via the FK) —
 * but alert/notification matching in this component should be treated
 * as unreliable until the backend inconsistencies above are resolved.
 * Do not use this component's alert counts as a source of truth for
 * clinical safety decisions until that happens.
 */
const PatientList = ({
  onSelectPatient,
  selectedPatient,
  onStatsUpdate,
  activeAlerts = [],
}) => {
  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [emergencyHistory, setEmergencyHistory] = useState([]);

  useEffect(() => {
    const fetchHistory = async () => {
      const { data } = await supabase
        .from("emergency_event_history_2024")
        .select("*")
        .eq("resolution_status", "pending");
      if (data) setEmergencyHistory(data);
    };
    fetchHistory();
  }, [activeAlerts]);

  const fetchPatients = async () => {
    setIsLoading(true);
    try {
      const platformUsers = await listPatients({ limit: 200 });

      const mapped = (platformUsers || []).map((u) => ({
        id: u.platform_user_id, // React key / selection identity — always present
        patientId: u.patient_id, // canonical link — null until Stage 2 exists for this user; used below for alert matching as best-available default, see file header caveat
        name: u.display_name || u.email || u.platform_user_id,
        age: null,
        riskLevel: "unknown",
        lastUpdate: "Not yet tracked",
        alerts: 0,
        condition: "",
        vitals: {},
      }));

      setPatients(mapped);

      if (onStatsUpdate) {
        const totalPatients = mapped.length;
        const activeAlertsCount =
          mapped.reduce((sum, p) => sum + (p.alerts || 0), 0) +
          activeAlerts.length;
        const criticalAlerts = mapped.filter(
          (p) => p.riskLevel === "high",
        ).length;
        onStatsUpdate({
          totalPatients,
          activeAlerts: activeAlertsCount,
          criticalAlerts,
        });
      }
    } catch (err) {
      console.error(
        "[PatientList] failed to load patients:",
        err?.response?.data?.message || err?.message || err,
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, [onStatsUpdate, activeAlerts.length]);

  useEffect(() => {
    const onRefresh = () => {
      fetchPatients();
    };
    window.addEventListener("patientListRefresh", onRefresh);
    return () => window.removeEventListener("patientListRefresh", onRefresh);
  }, [onStatsUpdate, activeAlerts.length]);

  // Matches against patientId (canonical) as the best available default.
  // See file header: this is NOT a verified-correct join for either
  // activeAlerts or emergencyHistory — treat matches/misses here as
  // unreliable until the backend identifier inconsistencies are fixed.
  const getPatientAlerts = (patientId) => {
    const relevantAlerts = activeAlerts.filter(
      (a) => String(a.patientId) === String(patientId),
    );
    const historyLink = emergencyHistory.find(
      (h) => String(h.patient_id) === String(patientId),
    );
    if (historyLink && relevantAlerts.some((a) => a.severity === "critical")) {
      return relevantAlerts.sort((a, b) => (a.protocol === "4422" ? -1 : 1));
    }
    return relevantAlerts;
  };

  const filteredPatients = useMemo(() => {
    return patients
      .filter((patient) => {
        const matchesSearch = patient.name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
        const patientSpecificAlerts = getPatientAlerts(patient.patientId);
        const hasActiveAlerts =
          patientSpecificAlerts.length > 0 || patient.alerts > 0;
        if (!matchesSearch) return false;
        switch (filter) {
          case "alerts":
            return hasActiveAlerts;
          case "high":
            return patient.riskLevel === "high";
          case "medium":
            return patient.riskLevel === "medium";
          case "low":
            return patient.riskLevel === "low";
          default:
            return true;
        }
      })
      .sort((a, b) => {
        const alertsA = getPatientAlerts(a.patientId).length + a.alerts;
        const alertsB = getPatientAlerts(b.patientId).length + b.alerts;
        return alertsB - alertsA;
      });
  }, [patients, searchTerm, filter, activeAlerts, emergencyHistory]);

  const getRiskColor = (level) => {
    switch (level) {
      case "high":
        return "text-red-600 bg-red-100 border-red-200";
      case "medium":
        return "text-orange-600 bg-orange-100 border-orange-200";
      case "low":
        return "text-green-600 bg-green-100 border-green-200";
      default:
        return "text-gray-600 bg-gray-100 border-gray-200";
    }
  };

  const getRiskIcon = (level) => {
    switch (level) {
      case "high":
        return FiAlertTriangle;
      case "medium":
        return FiClock;
      case "low":
        return FiCheck;
      case "unknown":
        return FiClock;
      default:
        return FiClock;
    }
  };

  const getRiskLabel = (level) => {
    if (!level || level === "unknown") return "Not yet tracked";
    return level;
  };

  return (
    // ↓ removed hardcoded w-[400px] and h-[600px] — now fluid inside parent column
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 w-full flex flex-col">
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Patient List</h2>
          <button
            type="button"
            onClick={() => {
              const event = new Event("patientListRefresh");
              window.dispatchEvent(event);
            }}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <FiRefreshCw
              className={`transition-transform ${isLoading ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
          {activeAlerts.length > 0 && (
            <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-1 rounded-full animate-pulse">
              {activeAlerts.length} Active
            </span>
          )}
        </div>
        {/* ↓ removed hardcoded w-[350px] — now full width */}
        <div className="relative mb-4">
          <SafeIcon
            icon={FiSearch}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            placeholder="Search patients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
          />
        </div>
        {/* ↓ removed hardcoded w-[300px] — now full width with wrapping */}
        <div className="flex flex-wrap gap-1.5">
          {[
            { id: "all", label: "All" },
            { id: "alerts", label: "Active Alerts", icon: FiActivity },
            { id: "high", label: "High Risk" },
            { id: "medium", label: "Medium" },
            { id: "low", label: "Low" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`px-2.5 py-1 text-xs rounded-full transition-colors whitespace-nowrap flex items-center gap-1 ${
                filter === tab.id
                  ? tab.id === "alerts"
                    ? "bg-red-100 text-red-700 font-medium ring-1 ring-red-300"
                    : "bg-blue-100 text-blue-700 font-medium"
                  : "text-gray-600 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              {tab.icon && <SafeIcon icon={tab.icon} className="text-[10px]" />}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="divide-y divide-gray-200 overflow-y-auto max-h-[520px]">
        {isLoading && (
          <div className="p-6 text-center text-sm text-gray-500">
            Loading patients...
          </div>
        )}
        <AnimatePresence>
          {filteredPatients.map((patient, index) => {
            const currentRealTimeAlerts = getPatientAlerts(patient.patientId);
            const hasAlerts =
              currentRealTimeAlerts.length > 0 || patient.alerts > 0;
            const isSelected = selectedPatient?.id === patient.id;
            let rowClassName = "p-4 cursor-pointer transition-all border-r-4 ";
            if (hasAlerts) rowClassName += "bg-red-50 border-red-500";
            else if (isSelected) rowClassName += "bg-blue-50 border-blue-500";
            else rowClassName += "hover:bg-gray-50 border-transparent";

            return (
              <motion.div
                key={patient.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2, delay: index * 0.05 }}
                onClick={() => onSelectPatient(patient)}
                className={rowClassName}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center relative flex-shrink-0 ${hasAlerts ? "bg-red-100 text-red-700" : "bg-gray-200 text-gray-700"}`}
                    >
                      <span className="text-sm font-bold">
                        {patient.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </span>
                      <div
                        className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${hasAlerts ? "bg-red-500" : "bg-green-500"}`}
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-semibold text-gray-900 leading-tight truncate">
                        {patient.name}
                      </h3>
                      <p className="text-xs text-gray-500">
                        ID: {patient.id}
                        {patient.age ? ` • Age ${patient.age}` : " • Age not yet tracked"}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-1 flex-shrink-0 ml-2">
                    <div
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center space-x-1 border ${getRiskColor(patient.riskLevel)}`}
                    >
                      <SafeIcon
                        icon={getRiskIcon(patient.riskLevel)}
                        className="text-[10px]"
                      />
                      <span>{getRiskLabel(patient.riskLevel)}</span>
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {patient.lastUpdate}
                    </div>
                  </div>
                </div>
                <div className="mt-1 pl-13">
                  {patient.condition && (
                    <div className="text-xs text-gray-600 mb-1">
                      {patient.condition}
                    </div>
                  )}
                  {hasAlerts && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {currentRealTimeAlerts.map((alert, idx) => (
                        <span
                          key={`rt-${idx}`}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 animate-pulse"
                        >
                          <SafeIcon
                            icon={FiActivity}
                            className="mr-1 text-[10px]"
                          />
                          {alert.type || "Vitals Alert"}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PatientList;